/**
 * Wording for the two tenders that share one Permata EDC terminal.
 *
 * WHY THIS EXISTS. One physical terminal takes a card (trx 01) AND a QRIS scan
 * (trx 11), and the backend distinguishes them by `paymentMethod` —
 * `edc-card` vs `edc-qris`. But `paymentGateway` is `edc-permata` for BOTH, and
 * the dialog picks its panel off the gateway. So every screen in the EDC flow
 * was written in card language and rendered verbatim for a QRIS payment:
 * "Masukkan kartu", "Kartu pelanggan mungkin sudah terdebit".
 *
 * That is not cosmetic. The line that matters most is the one shown when the
 * terminal never answers — the cashier is being told to go and check the
 * machine, and telling them to check a CARD transaction when the customer was
 * asked to scan a QR sends them looking for the wrong thing, in front of a
 * customer, on a payment that may have gone through.
 *
 * The same conflation shipped in the kiosk chooser (both EDC buttons read
 * "Kartu (EDC)") and is fixed there in `kiosk-edc-payment.js` from a table with
 * the same shape. The two clients are separate repos with no shared package, so
 * the table is replicated rather than imported — keep them in step.
 *
 * NOT tender-specific, deliberately: everything about the printed slip. A
 * QRIS-on-EDC transaction prints a slip carrying a trace number exactly as a
 * card sale does, and that slip is the only evidence a parked payment can be
 * resolved from.
 */

export type EdcTender = 'edc-card' | 'edc-qris';

interface TenderCopy {
  /** Dialog title. */
  dialogTitle: string;
  /** Heading while the terminal waits for the customer. */
  waitingTitle: string;
  /** What the customer is being asked to do. */
  waitingBody: string;
  /** Shown mid-transaction — what NOT to do while it runs. */
  processingBody: string;
  /**
   * Where the cancel actually is.
   *
   * The ECR protocol has no cancel message, so this screen cannot stop the
   * terminal and deliberately offers no button that pretends to. But the
   * terminal's OWN keypad can, and pressing it returns `E1`, which the backend
   * reads as a clean decline — seat released, retry offered. A cashier who does
   * not know that is stuck watching a screen.
   */
  cancelHint: string;
  /** Reassurance on a PROVEN decline. Nothing moved. */
  declinedBody: string;
  /**
   * The load-bearing line. Shown when the terminal went silent, where we do
   * NOT know whether the customer's money moved.
   */
  unresolvedBody: string;
}

const CARD: TenderCopy = {
  dialogTitle: 'Pembayaran kartu (EDC)',
  waitingTitle: 'Masukkan kartu di mesin EDC',
  waitingBody: 'Pelanggan memasukkan atau menempelkan kartu, lalu mengetik PIN.',
  processingBody: 'Jangan cabut kartu atau matikan mesin.',
  cancelHint: 'Untuk membatalkan, tekan CANCEL di mesin EDC — bukan di layar ini.',
  declinedBody: 'Kartu pelanggan tidak terdebit. Coba kartu lain.',
  unresolvedBody: 'Kartu pelanggan mungkin sudah terdebit.',
};

const QRIS: TenderCopy = {
  dialogTitle: 'Pembayaran QRIS (EDC)',
  waitingTitle: 'Pelanggan memindai QRIS di mesin EDC',
  waitingBody: 'Pelanggan memindai kode QR di layar mesin dengan aplikasi pembayarannya.',
  processingBody: 'Jangan matikan mesin.',
  cancelHint: 'Untuk membatalkan, tekan CANCEL di mesin EDC — bukan di layar ini.',
  declinedBody: 'Dana pelanggan tidak terpotong. Coba lagi atau pilih metode lain.',
  unresolvedBody: 'Pembayaran QRIS pelanggan mungkin sudah masuk.',
};

/** Card is the default: an unknown tender must not claim a QR was scanned. */
export function edcCopyFor(tender?: EdcTender | string | null): TenderCopy {
  return String(tender ?? '').toLowerCase() === 'edc-qris' ? QRIS : CARD;
}

export const _test = { CARD, QRIS };
